import { useState, useMemo } from "react";
import {
  useCostSummary,
  useCostTrends,
  useCostByService,
  useCostByServiceEnv,
  useUnattributedCosts,
} from "../hooks/useCosts";
import PageHeader from "../components/common/PageHeader";
import MetricCard from "../components/common/MetricCard";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { format } from "date-fns";

const PROVIDER_COLORS: Record<string, string> = {
  aws: "#FF9900",
  azure: "#0078D4",
  gcp: "#4285F4",
  kubernetes: "#326CE5",
};

const PIE_COLORS = ["#FF9900", "#0078D4", "#4285F4", "#326CE5", "#10B981", "#8B5CF6"];

const GROUP_OPTIONS = [
  { value: "", label: "All groups" },
  { value: "backend", label: "Backend" },
  { value: "frontend", label: "Frontend" },
  { value: "data_eng", label: "Data Eng" },
  { value: "advisor", label: "Advisor" },
  { value: "analytics", label: "Analytics" },
  { value: "dcc", label: "DCC" },
  { value: "platform", label: "Platform" },
  { value: "other", label: "Other" },
];

function formatCurrency(value: number | null | undefined): string {
  const v = value ?? 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);
}

export default function CostAnalytics() {
  const [periodDays, setPeriodDays] = useState(30);
  const [group, setGroup] = useState("");

  const {
    data: summary,
    isLoading,
    error,
    refetch,
  } = useCostSummary(periodDays);
  const { data: trend } = useCostTrends(periodDays);
  const { data: byService } = useCostByService(periodDays);
  const { data: byServiceEnv } = useCostByServiceEnv(periodDays, group || undefined);
  const { data: unattributed } = useUnattributedCosts(periodDays);

  const providerPieData = Object.entries(summary?.by_provider ?? {}).map(
    ([name, value]) => ({ name: name.toUpperCase(), value: Number(value) })
  );

  const trendChartData = (trend?.data_points ?? []).map((dp) => ({
    date: format(new Date(dp.period_start), "MMM d"),
    total: dp.total_cost,
    ...dp.by_provider,
  }));

  const sortedServices = [...(byService ?? [])].sort(
    (a, b) => b.total_cost - a.total_cost
  );

  const serviceEnvRows = useMemo(() => {
    const bucket = new Map<
      string,
      {
        slug: string;
        name: string;
        group: string | null;
        env: string;
        aws: number;
        azure: number;
        gcp: number;
        kubernetes: number;
        total: number;
      }
    >();
    for (const r of byServiceEnv ?? []) {
      const slug = r.service_slug ?? "unattributed";
      const key = `${slug}::${r.environment}`;
      const existing = bucket.get(key) ?? {
        slug,
        name: r.service_name ?? slug,
        group: r.service_group ?? null,
        env: r.environment,
        aws: 0,
        azure: 0,
        gcp: 0,
        kubernetes: 0,
        total: 0,
      };
      const cost = Number(r.total_cost) || 0;
      if (r.cloud_provider === "aws") existing.aws += cost;
      else if (r.cloud_provider === "azure") existing.azure += cost;
      else if (r.cloud_provider === "gcp") existing.gcp += cost;
      else if (r.cloud_provider === "kubernetes") existing.kubernetes += cost;
      existing.total += cost;
      bucket.set(key, existing);
    }
    return [...bucket.values()].sort((a, b) => b.total - a.total);
  }, [byServiceEnv]);

  const unattributedTotal = (unattributed ?? []).reduce(
    (sum, row) => sum + (row.total_cost ?? 0),
    0
  );

  if (isLoading) return <LoadingSpinner message="Loading cost data..." />;
  if (error)
    return <ErrorBanner message="Failed to load costs" onRetry={refetch} />;

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        title="Cost Analytics"
        description="Multi-cloud cost broken down by service, environment, and provider."
      >
        <select
          value={periodDays}
          onChange={(e) => setPeriodDays(Number(e.target.value))}
          className="select-field"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </PageHeader>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total Cost"
          value={formatCurrency(summary?.total_cost ?? 0)}
          subtext={
            trend?.total_change_pct != null
              ? `${trend.total_change_pct > 0 ? "+" : ""}${trend.total_change_pct.toFixed(1)}% vs prior`
              : undefined
          }
        />
        <MetricCard
          label="AWS"
          value={formatCurrency(summary?.by_provider?.aws ?? 0)}
        />
        <MetricCard
          label="Azure"
          value={formatCurrency(summary?.by_provider?.azure ?? 0)}
        />
        <MetricCard
          label="GCP"
          value={formatCurrency(summary?.by_provider?.gcp ?? 0)}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Cost Trend */}
        <div className="lg:col-span-2 card">
          <div className="card-header">
            <h3 className="text-card-title text-slate-900 dark:text-white">
              Cost Trend
            </h3>
          </div>
          <div className="card-body">
            {trendChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" fontSize={12} stroke="#94a3b8" />
                  <YAxis
                    fontSize={12}
                    stroke="#94a3b8"
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip
                    formatter={(value: number) => formatCurrency(value)}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="total" stroke="#6366f1" strokeWidth={2} dot={false} name="Total" />
                  <Line type="monotone" dataKey="aws" stroke="#FF9900" strokeWidth={1.5} dot={false} name="AWS" />
                  <Line type="monotone" dataKey="azure" stroke="#0078D4" strokeWidth={1.5} dot={false} name="Azure" />
                  <Line type="monotone" dataKey="gcp" stroke="#4285F4" strokeWidth={1.5} dot={false} name="GCP" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400 dark:text-slate-500">
                No trend data available
              </div>
            )}
          </div>
        </div>

        {/* Cost by Provider Pie */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-card-title text-slate-900 dark:text-white">
              By Provider
            </h3>
          </div>
          <div className="card-body">
            {providerPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={providerPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
                    }
                  >
                    {providerPieData.map((entry, idx) => (
                      <Cell
                        key={entry.name}
                        fill={
                          PROVIDER_COLORS[entry.name.toLowerCase()] ??
                          PIE_COLORS[idx % PIE_COLORS.length]
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400 dark:text-slate-500">
                No provider data
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cost by Service × Env */}
      <div className="card">
        <div className="card-header flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-card-title text-slate-900 dark:text-white">
              Cost by Service × Environment
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Each row is one (service, env) pair. Filter by service group below.
            </p>
          </div>
          <select
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            className="select-field"
          >
            {GROUP_OPTIONS.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </select>
        </div>
        <div className="card-body p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-50 dark:bg-slate-800/50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              <tr>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3">Group</th>
                <th className="px-4 py-3">Environment</th>
                <th className="px-4 py-3 text-right">AWS</th>
                <th className="px-4 py-3 text-right">Azure</th>
                <th className="px-4 py-3 text-right">GCP</th>
                <th className="px-4 py-3 text-right">K8s</th>
                <th className="px-4 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {serviceEnvRows.slice(0, 50).map((row) => (
                <tr key={`${row.slug}-${row.env}`} className="hover:bg-surface-50 dark:hover:bg-slate-800/30 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{row.name}</td>
                  <td className="px-4 py-3">
                    {row.group ? (
                      <span className="inline-flex items-center rounded-full bg-surface-100 dark:bg-slate-700 px-2 py-0.5 text-xs font-medium text-slate-700 dark:text-slate-300 capitalize">
                        {row.group.replace("_", " ")}
                      </span>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-md bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                      {row.env}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400 tabular-nums">{formatCurrency(row.aws)}</td>
                  <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400 tabular-nums">{formatCurrency(row.azure)}</td>
                  <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400 tabular-nums">{formatCurrency(row.gcp)}</td>
                  <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400 tabular-nums">{formatCurrency(row.kubernetes)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-white tabular-nums">
                    {formatCurrency(row.total)}
                  </td>
                </tr>
              ))}
              {serviceEnvRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-400 dark:text-slate-500">
                    No per-service cost data yet — link cloud accounts to services on the Service Catalog page.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Unattributed costs */}
      {(unattributed?.length ?? 0) > 0 && (
        <div className="rounded-2xl border border-amber-200 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-950/30 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-card-title text-amber-900 dark:text-amber-200">
              {formatCurrency(unattributedTotal)} unattributed cost ({unattributed?.length} accounts)
            </h3>
            <span className="text-xs text-amber-700 dark:text-amber-400">Map these accounts to services to get full visibility</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-[11px] font-semibold uppercase tracking-wider text-amber-900 dark:text-amber-300">
                <tr>
                  <th className="px-2 py-1.5">Provider</th>
                  <th className="px-2 py-1.5">Account ID</th>
                  <th className="px-2 py-1.5">Display Name</th>
                  <th className="px-2 py-1.5 text-right">Cost ({periodDays}d)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-100 dark:divide-amber-800/30">
                {(unattributed ?? []).slice(0, 10).map((row) => (
                  <tr key={`${row.cloud_provider}-${row.account_id}`} className="hover:bg-amber-100/50 dark:hover:bg-amber-900/20 transition-colors">
                    <td className="px-2 py-1.5 uppercase text-amber-700 dark:text-amber-400">{row.cloud_provider}</td>
                    <td className="px-2 py-1.5 font-mono text-amber-900 dark:text-amber-200">{row.account_id}</td>
                    <td className="px-2 py-1.5 text-amber-700 dark:text-amber-400">{row.display_name ?? "—"}</td>
                    <td className="px-2 py-1.5 text-right font-semibold text-amber-900 dark:text-amber-200 tabular-nums">
                      {formatCurrency(row.total_cost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cost by Service Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-card-title text-slate-900 dark:text-white">
            Cost by Service (rolled up across all environments)
          </h3>
        </div>
        <div className="card-body p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-50 dark:bg-slate-800/50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              <tr>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3 text-right">Total Cost</th>
                <th className="px-4 py-3 text-right">AWS</th>
                <th className="px-4 py-3 text-right">Azure</th>
                <th className="px-4 py-3 text-right">GCP</th>
                <th className="px-4 py-3 text-right">K8s</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {sortedServices.slice(0, 20).map((svc) => (
                <tr
                  key={svc.service_slug ?? svc.service_id ?? "unattributed"}
                  className="hover:bg-surface-50 dark:hover:bg-slate-800/30 transition-colors"
                >
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                    {svc.service_name ?? svc.service_slug ?? "Unassigned"}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-white tabular-nums">
                    {formatCurrency(svc.total_cost)}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400 tabular-nums">
                    {formatCurrency(svc.by_provider?.aws ?? 0)}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400 tabular-nums">
                    {formatCurrency(svc.by_provider?.azure ?? 0)}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400 tabular-nums">
                    {formatCurrency(svc.by_provider?.gcp ?? 0)}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400 tabular-nums">
                    {formatCurrency(svc.by_provider?.kubernetes ?? 0)}
                  </td>
                </tr>
              ))}
              {sortedServices.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400 dark:text-slate-500">
                    No cost data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
